import type React from "react";
import { useEffect, useRef } from "react";

/**
 * <dialog> 를 React 상태(open)에 물려 주는 훅.
 *
 * * 랜딩 모달은 원본 HTML 이 네이티브 <dialog> 를 쓰고 있어 그대로 유지했다.
 * ::backdrop, ESC 닫기, 포커스 트랩을 브라우저가 처리해 줘서 modal/ 의 수동 구현보다
 * 접근성이 낫습니다. showModal()/close() 는 명령형이라 여기서만 상태와 동기화합니다.
 */
export default function useDialog(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // ESC 로 닫히면 dialog 가 close 이벤트만 쏘고 React 상태는 그대로라, 되돌려 줍니다.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handle = () => onClose();
    dialog.addEventListener("close", handle);
    return () => dialog.removeEventListener("close", handle);
  }, [onClose]);

  /** 카드 바깥(백드롭) 클릭 시 닫기 */
  const onBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === ref.current) onClose();
  };

  return { ref, onBackdropClick };
}
